import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ActionIconDef, GridConfig } from '@inavista/components/grid';
import { REMOTE_SEARCH_ROW_KEY } from 'projects/service/src/app/shared/utils/remote-search-grid-api';

// Customer advanced search (Service Order, ISC, books, cart checkout and leads popups) on @inavista/components.
// Same columns, labels, order, widths, alignment and toolbar (Search, Clear filters) as the Syncfusion grid it
// replaces. Rows come from the same endpoints with the same request (RemoteSearchGridApi); 50 rows per request,
// loaded as the user scrolls. No paging (Martin).

export const CUSTOMER_ADVANCED_SEARCH_PERSISTENCE_KEY = 'rims.service.customer-advanced-search';
export const CUSTOMER_ADVANCED_SEARCH_URL = 'rims-search://customer-advanced-search';
export const PROCEED_ACTION = 'proceed';

// gridType 'config' (every caller today): CustomerSearchViewModel.
export const CUSTOMER_COLUMNS: [string, string][] = [
  ['soldToBP', 'nvarchar'],
  ['bpDescription', 'nvarchar'],
  ['address', 'nvarchar'],
  ['city', 'nvarchar'],
  ['stateProvince', 'nvarchar'],
  ['zipCode', 'nvarchar'],
  ['serviceDepartment', 'nvarchar'],
  ['siteName', 'nvarchar'],
  ['telephone', 'nvarchar'],
  ['country', 'nvarchar'],
  ['eMail', 'nvarchar'],
  ['isNationalAccount', 'bit'],
  ['nationalAccount', 'nvarchar'],
];

// Any other gridType (no caller sets one): SoldToBP/GetSoldToBP.
export const SOLD_TO_BP_COLUMNS: [string, string][] = [
  ['soldToBP', 'nvarchar'],
  ['bpDescription', 'nvarchar'],
  ['addressCode', 'nvarchar'],
];

const ARROW_RIGHT_ICON = '<line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />';

// The old command column: one "Proceed" arrow, frozen right, 120px.
export const PROCEED_ACTION_ICONS: ActionIconDef[] = [
  { key: PROCEED_ACTION, label: 'Proceed', icon: ARROW_RIGHT_ICON, colorFn: () => '#000000de' },
];

export function buildCustomerAdvancedSearchGridConfig(isConfig: boolean, emptyStateMessage: string): GridConfig {
  const columns = isConfig ? CUSTOMER_COLUMNS : SOLD_TO_BP_COLUMNS;
  const order = columns.map(([c]) => c);
  return {
    apiBaseUrl: CUSTOMER_ADVANCED_SEARCH_URL,
    keyColumn: REMOTE_SEARCH_ROW_KEY,
    // The old page size.
    pageSize: 50,
    rowHeight: 30,
    defaultColumnOrder: order,
    // National Account (the flag) was hidden and out of the column chooser; the row key is ours.
    hiddenByDefaultColumns: isConfig ? ['isNationalAccount', REMOTE_SEARCH_ROW_KEY] : [REMOTE_SEARCH_ROW_KEY],
    columnLabels: isConfig ? {
      soldToBP: 'ID', bpDescription: 'Business Partner', address: 'Address', city: 'City', stateProvince: 'State/Province',
      zipCode: 'Zip Code', serviceDepartment: 'Service Department', siteName: 'Site Name', telephone: 'Telephone', country: 'Country',
      eMail: 'Email', isNationalAccount: 'National Account', nationalAccount: 'National Account',
    } : { soldToBP: 'ID', bpDescription: 'Business Partner Description', addressCode: 'Address Code' },
    // Every column left, as the old grid (no textAlign).
    columnAlign: Object.fromEntries(order.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    // ID was 120px; the others had no width and shared the rest, so they start equal and stretch.
    columnWidths: Object.fromEntries(order.map(c => [c, c === 'soldToBP' ? 120 : 150])),
    stretchColumns: true,
    columnSizing: { soldToBP: { noStretch: true } },
    columnTypes: isConfig ? { isNationalAccount: 'boolean' } : {},
    // City, State/Province and Country had the Excel (checkbox) filter; the rest the Menu filter.
    excelFilterColumns: isConfig ? ['city', 'stateProvince', 'country'] : [],
    // The old Actions column was 120px wide.
    actionsColumnMinWidth: 120,
    searchHint: 'Searches every column.',
    // The old toolbar: Search and Clear all Filters only.
    enableColumnsPicker: false,
    enableExport: false,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableRefresh: false,
    // Reset layout undoes the layout the grid now remembers (as on the other Service Order popup grids).
    // Ctrl+click adds a sort column, as the old grid (Syncfusion multi-sort; 0.3.42, N80).
    multiSort: true,
    enableResetLayout: true,
    // The footer gives the old "Total Records" count.
    enableFooter: true,
    loadingIndicator: 'banner',
    emptyStateMessage,
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CUSTOMER_ADVANCED_SEARCH_PERSISTENCE_KEY,
    persistenceVersion: 2, // 2: Site Name column (a saved layout would hide it)
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
